// Shared utilities and primitives for both landscape and mobile team assessment PDFs.
// Update here and both formats pick it up automatically.

export function scoreColor(score) {
  if (score >= 4) return '#2d6a4f';
  if (score >= 3) return '#f4a261';
  return '#e63946';
}

export function scoreLabel(score) {
  if (score >= 4.5) return 'Excellent';
  if (score >= 3.5) return 'Good';
  if (score >= 2.5) return 'Needs Attention';
  return 'Critical';
}

// BehaviorRow — used in Highlights and All Behaviors pages for both formats.
// mobile=true renders a taller, single-column-friendly row.
export function BehaviorRow({ b, rank, highlight, mobile }) {
  const bg = highlight === 'top'
    ? 'rgba(45,106,79,0.07)'
    : highlight === 'bottom'
    ? 'rgba(230,57,70,0.06)'
    : 'white';

  const scoreCol = highlight === 'top' ? '#2d6a4f' : highlight === 'bottom' ? '#e63946' : scoreColor(b.avg ?? 0);

  if (mobile) {
    return (
      <div style={{ background: bg, borderRadius: 6, padding: '8px 12px', marginBottom: 5, display: 'flex', alignItems: 'center', gap: 10 }}>
        {rank !== undefined && (
          <div style={{ width: 18, textAlign: 'center', fontSize: 9, color: '#6b757e', fontWeight: 700, flexShrink: 0, fontFamily: 'Montserrat, sans-serif' }}>{rank}</div>
        )}
        <div style={{ width: 7, height: 7, borderRadius: '50%', background: b.color, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, color: '#1e2e45', lineHeight: 1.35 }}>{b.text}</div>
          <div style={{ fontSize: 8, color: '#6b757e', marginTop: 2, fontFamily: 'Montserrat, sans-serif' }}>{b.category}</div>
        </div>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 14, fontWeight: 700, color: scoreCol, flexShrink: 0, minWidth: 26, textAlign: 'right' }}>
          {b.avg !== null ? b.avg.toFixed(1) : '—'}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 6, marginBottom: 4, background: bg }}>
      {rank !== undefined && (
        <div style={{ width: 20, textAlign: 'center', fontSize: 9, color: '#6b757e', fontWeight: 700, flexShrink: 0, fontFamily: 'Montserrat, sans-serif' }}>{rank}</div>
      )}
      <div style={{ width: 7, height: 7, borderRadius: '50%', background: b.color, flexShrink: 0 }} />
      <div style={{ flex: 1, fontSize: 9.5, color: '#1e2e45', lineHeight: 1.4 }}>{b.text}</div>
      <div style={{ fontSize: 8, color: '#6b757e', marginRight: 6, flexShrink: 0, fontFamily: 'Montserrat, sans-serif' }}>{b.category}</div>
      <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 14, fontWeight: 700, color: scoreCol, minWidth: 28, textAlign: 'right', flexShrink: 0 }}>
        {b.avg !== null ? b.avg.toFixed(1) : '—'}
      </div>
    </div>
  );
}

// Compact behavior row for "All Behaviors" pages where space is tight.
export function BehaviorRowCompact({ b, mobile }) {
  if (b.avg === null) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: mobile ? '5px 8px' : '4px 8px', borderRadius: 4, marginBottom: 3, opacity: 0.4 }}>
        <div style={{ flex: 1, fontSize: mobile ? 9 : 8.5, color: '#1e2e45', lineHeight: 1.3 }}>{b.text}</div>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 11, color: '#6b757e', minWidth: 24, textAlign: 'right' }}>—</div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: mobile ? '5px 8px' : '4px 8px', borderRadius: 4, marginBottom: 3, background: 'white' }}>
      <div style={{ flex: 1, fontSize: mobile ? 9 : 8.5, color: '#1e2e45', lineHeight: 1.3 }}>{b.text}</div>
      <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 11, fontWeight: 700, color: scoreColor(b.avg), minWidth: 24, textAlign: 'right', flexShrink: 0 }}>
        {b.avg.toFixed(1)}
      </div>
    </div>
  );
}

// Eyebrow label used at the top of every page.
export function PageEyebrow({ dark }) {
  return (
    <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '2px', color: dark ? 'rgba(255,255,255,0.4)' : '#5f7942', textTransform: 'uppercase', marginBottom: 6, fontFamily: 'Montserrat, sans-serif' }}>
      Team Assessment Report
    </div>
  );
}

// Section heading used on data pages.
export function PageHeading({ children }) {
  return (
    <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, fontWeight: 700, color: '#1e2e45', marginBottom: 20 }}>
      {children}
    </div>
  );
}
